import { type FormEvent, useState } from 'react';
import { Alert, Button, Field } from '../components/ui';
import { t } from '../i18n';
import { ApiError, api, setAccessToken } from './api';
import { errorMessage } from './errors';

/**
 * Acciones sensibles (guía 5.5): si la API responde 403 "mfa_step_up_required", se pide un código, se
 * re-autentica (POST /api/auth/2fa/step-up) y se reintenta la acción una vez.
 */
export function useStepUp() {
  const [pending, setPending] = useState<null | { resolve: () => void; reject: (e: unknown) => void }>(null);

  async function run<T>(action: () => Promise<T>): Promise<T> {
    try {
      return await action();
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 403 && err.code === 'mfa_step_up_required')) throw err;
      await new Promise<void>((resolve, reject) => setPending({ resolve, reject }));
      return action();
    }
  }

  const prompt = pending ? (
    <StepUpPrompt
      onDone={() => {
        pending.resolve();
        setPending(null);
      }}
      onCancel={() => {
        pending.reject(new Error('cancelado'));
        setPending(null);
      }}
    />
  ) : null;

  return { run, prompt };
}

function StepUpPrompt({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ accessToken: string }>('/api/auth/2fa/step-up', { json: { code: code.trim() } });
      setAccessToken(res.accessToken);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="stepup-titulo" className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-lg bg-white p-6 shadow-xl">
        <h2 id="stepup-titulo" className="text-base font-semibold">
          {t('mfa.titulo')}
        </h2>
        <p className="text-sm text-slate-600">{t('seguridad.reautenticar')}</p>
        {error && <Alert>{error}</Alert>}
        <Field id="stepup-codigo" label={t('mfa.codigo')} autoComplete="one-time-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t('comun.cancelar')}
          </Button>
          <Button type="submit" disabled={busy || code.trim().length < 6}>
            {t('mfa.verificar')}
          </Button>
        </div>
      </form>
    </div>
  );
}
